import React, { useState, useEffect } from 'react';
import { X, History, Download, Loader2 } from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import styles from './HistoryModal.module.css';

const HistoryModal = ({ isOpen, onClose, submissionId, poNumber }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && submissionId) {
      fetchHistory();
    }
  }, [isOpen, submissionId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/design-submissions/${submissionId}/history`);
      const data = response.data.data || response.data || [];
      const sortedData = Array.isArray(data) ? [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : [];
      setHistory(sortedData);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (submission) => {
    try {
      const response = await api.get(`/design-submissions/${submission.id}/file`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = submission.pdf_filename || submission.pdf_filepath?.split('/').pop() || 'design.pdf';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const getStatusClass = (status) => {
    if (!status) return styles['status-pending'];
    switch (status.toLowerCase()) {
      case 'approved': return styles['status-approved'];
      case 'rejected': return styles['status-rejected'];
      case 'intimate': return styles['status-intimate'];
      case 'in_revision':
      case 'revision': return styles['status-revision'];
      case 'in_review': return styles['status-pending'];
      default: return styles['status-pending'];
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['modal-overlay']} onClick={onClose}>
      <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
        <div className={styles['modal-header']}>
          <div className={styles['header-title']}>
            <div className={styles['icon-box']}>
              <History size={20} />
            </div>
            <div>
              <h3>Version History</h3>
              <p>LOI Number: <span className={styles['po-highlight']}>{poNumber || '---'}</span></p>
            </div>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles['modal-body']}>
          {loading ? (
            <div className={styles['loading-state']}>
              <Loader2 className={styles['spinner']} />
              <span>Fetching history records...</span>
            </div>
          ) : history.length > 0 ? (
            <div className={styles['table-wrapper']}>
              <table className={styles['history-table']}>
                <thead>
                  <tr>
                    <th>VERSION</th>
                    <th>SUBMISSION TO CLIENT</th>
                    <th>INTIMATE DATE</th>
                    <th>CLIENT REV TO CONSULTANT</th>
                    <th>SUBMISSION TO CONSULTANT</th>
                    <th>STATUS</th>
                    <th>INTIMATE REMARK</th>
                    <th>CREATED BY</th>
                    <th>CREATED AT</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={h.id || i}>
                      <td className={styles['version-cell']}>v{h.version_number || '1.0'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {h.submission_to_client_date ? new Date(h.submission_to_client_date).toLocaleDateString('en-IN') : '---'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {h.intimate_date ? new Date(h.intimate_date).toLocaleDateString('en-IN') : '---'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {h.client_rev_sub_to_consultant_date ? new Date(h.client_rev_sub_to_consultant_date).toLocaleDateString('en-IN') : '---'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {h.submission_to_consultant_date ? new Date(h.submission_to_consultant_date).toLocaleDateString('en-IN') : '---'}
                      </td>
                      <td>
                        <span className={`${styles['status-pill']} ${getStatusClass(h.status)}`}>
                          {(h.status || 'pending').replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className={styles['remark-cell']}>
                        {h.intimate_remark || '---'}
                      </td>
                      <td>
                        <div className={styles['user-info']}>
                          <div className={styles['avatar']}>
                            {h.creator?.name?.charAt(0) || 'U'}
                          </div>
                          <span>{h.creator?.name || 'User'}</span>
                        </div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#94a3b8' }}>
                        {h.created_at ? new Date(h.created_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }) : '---'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles['empty-state']}>
              <p>No historical versions found for this submission.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryModal;
